import NotificationItem, { LeadingIcon } from 'components/notification/NotificationItem';
import type { Notification } from 'types/notification';

const TIME_BUFF_PINK = '#FF4391';

/** 등급별 아이콘이 있는 카테고리. 그 밖에는 코인 아이콘으로 둔다. */
const TIER_ICONS = ['lite', 'membership', 'premium'];

const BUTTON_BASE = 'typography-caption-head me-auto mt-[6px] inline-flex w-fit items-center justify-center rounded-full px-2 py-[6px]';

const ShopButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <div className={`${BUTTON_BASE} bg-[#CBC3FF] text-[#6200EE]`} onClick={onClick}>
    {brsc.go_to_shop}
  </div>
);

const AlertButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <div className={`${BUTTON_BASE} bg-[#FCD6D7] text-[#F03439]`} onClick={onClick}>
    {label}
  </div>
);

function tierIcon(category: string): string {
  return TIER_ICONS.includes(category) ? `/imgweb/notification/ic_noti_${category}.jpg` : '/imgweb/notification/ic_noti_coin.jpg';
}

interface PurchaseItemProps {
  item: Notification;
  showUnreadHeader?: boolean;
  onShopClick: () => void;
  onInquiryClick: () => void;
  onLuckyboxClick: () => void;
  onTimeBuffClick: () => void;
}

const PurchaseItem: React.FC<PurchaseItemProps> = ({ item, showUnreadHeader, onShopClick, onInquiryClick, onLuckyboxClick, onTimeBuffClick }) => {
  const common = { unread: item.unread, showUnreadHeader, createdTime: item.createdTime };

  if (item.subtype === 'purchaseCoin') {
    // paid 가 아닌 건(billingComplete·notPaid)은 전부 지급이 안 끝난 것으로 안내한다.
    if (item.status === 'paid') {
      return (
        <NotificationItem
          {...common}
          leading={<LeadingIcon src="/imgweb/notification/ic_noti_coin.jpg" />}
          title={brsc.coin_charge_completed}
          content={brsc.noti_charged_coins.replace('$param', String(item.amount ?? 0))}
        />
      );
    }
    return (
      <NotificationItem
        {...common}
        leading={<LeadingIcon src="/imgweb/notification/ic_noti_coin.jpg" />}
        title={brsc.coin_charge_failed}
        content={<span dangerouslySetInnerHTML={{ __html: brsc.noti_coins_charge_failed }} />}
        bottom={
          <div className="flex flex-col">
            <span className="typography-body-sm mb-1 text-base-txt-secondary">{brsc.noti_charge_failed_step_1}</span>
            <ShopButton onClick={onShopClick} />
            <div className="h-3" />
            <span className="typography-body-sm mb-1 text-base-txt-secondary">{brsc.noti_charge_failed_step_2}</span>
            <AlertButton label={brsc.restrictions_contact} onClick={onInquiryClick} />
          </div>
        }
      />
    );
  }

  if (item.subtype === 'supplyCoin') {
    return (
      <NotificationItem
        {...common}
        leading={<LeadingIcon src={tierIcon(item.category)} />}
        title={item.category.toUpperCase()}
        content={brsc.noti_charged_coins.replace('$param', String(item.amount ?? 0))}
      />
    );
  }

  if (item.subtype === 'supplyLuckybox') {
    return (
      <NotificationItem
        {...common}
        onClick={onLuckyboxClick}
        leading={<LeadingIcon src={tierIcon(item.category)} />}
        title={item.category.toUpperCase()}
        content={brsc.noti_awarded_luckyboxes.replace('$param', String(item.amount ?? 0))}
        trailing={<span className="icon-chevron-right self-end text-base-txt-secondary" />}
      />
    );
  }

  if (item.subtype === 'purchaseTimeBuff') {
    return (
      <NotificationItem
        {...common}
        leading={<LeadingIcon src="/imgweb/sale/infinity_silver.png" background={TIME_BUFF_PINK} />}
        title={brsc.time_buff}
        content={brsc.pc_noti_center.replace('$param', String(item.count ?? 0))}
        bottom={<AlertButton label={brsc.btn_region_popup} onClick={onTimeBuffClick} />}
      />
    );
  }

  // compensateCoin
  const content = brsc.noti_compensation_content
    .replace('$param2', String(item.amount ?? 0))
    .replace('$param', makeDisplayTimeStr(item.billingCompletedAt ?? item.createdTime));

  return (
    <NotificationItem
      {...common}
      leading={<LeadingIcon src="/imgweb/notification/ic_noti_service.jpg" />}
      title={<span className="text-[#6200EE]">{brsc.noti_compensation_title}</span>}
      content={<span dangerouslySetInnerHTML={{ __html: content }} />}
    />
  );
};

export default PurchaseItem;
